package org.gymly.ui.admin

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.ExpandLess
import androidx.compose.material.icons.outlined.ExpandMore
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.PickedMediaHolder
import org.gymly.model.AdminPaymentMethodType
import org.gymly.model.AdminPaymentMethodTypeCreate
import org.gymly.model.AdminPaymentMethodTypeUpsert
import org.gymly.model.AdminPaypalMode
import org.gymly.model.AdminSettingsPaymentUpsert
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.FileUploadIconButton
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.rememberPasswordVisible
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage

private data class PaymentMethodDraft(
    val title: String,
    val description: String,
    val enabled: Boolean,
    val sortOrder: Int,
    val requiresPhone: Boolean,
    val requiresReference: Boolean,
    val requiresProof: Boolean,
    val autoApprove: Boolean,
    val amountLabel: String,
    val imageUrl: String?,
    val usesForeignCurrency: Boolean = false,
    val foreignCurrencyCode: String = "",
)

@Composable
internal fun SettingsPaymentMethodsPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var savingCode by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var methods by remember { mutableStateOf<List<AdminPaymentMethodType>>(emptyList()) }
    var expandedCode by remember { mutableStateOf<String?>(null) }
    val drafts = remember { mutableStateMapOf<String, PaymentMethodDraft>() }
    val imageHolders = remember { mutableStateMapOf<String, PickedMediaHolder>() }
    var imageRevision by remember { mutableStateOf(0) }

    var paypalEnabled by remember { mutableStateOf(false) }
    var paypalClientId by remember { mutableStateOf("") }
    var paypalSecret by remember { mutableStateOf("") }
    var paypalMode by remember { mutableStateOf(AdminPaypalMode.SANDBOX) }
    var stripeEnabled by remember { mutableStateOf(false) }
    var stripePublicKey by remember { mutableStateOf("") }
    var stripeSecretKey by remember { mutableStateOf("") }
    var modeMenu by remember { mutableStateOf(false) }
    var creatingType by remember { mutableStateOf(false) }
    var newCode by remember { mutableStateOf("") }
    var newTitle by remember { mutableStateOf("") }
    var deleteConfirmCode by remember { mutableStateOf<String?>(null) }

    fun applyBundle(list: List<AdminPaymentMethodType>, gateways: org.gymly.model.AdminSettingsPayment) {
        methods = list
        drafts.clear()
        list.forEach { m ->
            drafts[m.code] = PaymentMethodDraft(
                title = m.title,
                description = m.description.orEmpty(),
                enabled = m.enabled,
                sortOrder = m.sortOrder,
                requiresPhone = m.requiresPhone,
                requiresReference = m.requiresReference,
                requiresProof = m.requiresProof,
                autoApprove = m.autoApprove,
                amountLabel = m.amountLabel.orEmpty(),
                imageUrl = m.imageUrl,
                usesForeignCurrency = m.usesForeignCurrency,
                foreignCurrencyCode = m.foreignCurrencyCode.orEmpty(),
            )
        }
        paypalEnabled = gateways.paypalEnabled
        paypalClientId = gateways.paypalClientId.orEmpty()
        paypalSecret = gateways.paypalSecret.orEmpty()
        paypalMode = gateways.paypalMode
        stripeEnabled = gateways.stripeEnabled
        stripePublicKey = gateways.stripePublicKey.orEmpty()
        stripeSecretKey = gateways.stripeSecretKey.orEmpty()
    }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            success = null
            try {
                val bundle = AdminRepo.api.listPaymentMethodTypes()
                applyBundle(bundle.methods, bundle.gateways)
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los tipos de pago.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    VerticalScrollColumn(
        Modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(if (compact) 16.dp else 24.dp),
    ) {
        Text("Tipos de pago", fontWeight = FontWeight.Bold, fontSize = 18.sp)
        Text(
            "Personaliza imagen, título, descripción y campos de cada método. Stripe y PayPal requieren credenciales API.",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
            modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
        )

        when {
            loading -> Box(Modifier.fillMaxWidth().padding(40.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            else -> {
                error?.let {
                    Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
                }
                success?.let {
                    Text(it, color = AdminColors.Success, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
                }

                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    OutlinedButton(
                        onClick = { creatingType = !creatingType },
                        enabled = savingCode == null,
                        shape = RoundedCornerShape(10.dp),
                    ) {
                        Text(if (creatingType) "Cancelar" else "Agregar tipo")
                    }
                }

                if (creatingType) {
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .padding(bottom = 16.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
                            .background(AdminColors.Surface)
                            .padding(14.dp),
                    ) {
                        Text("Nuevo tipo de pago", fontWeight = FontWeight.SemiBold)
                        Spacer(Modifier.height(10.dp))
                        PaymentMethodField("Código (ej. CRYPTO)", newCode) { newCode = it.uppercase() }
                        Spacer(Modifier.height(8.dp))
                        PaymentMethodField("Título", newTitle) { newTitle = it }
                        Spacer(Modifier.height(12.dp))
                        Button(
                            onClick = {
                                scope.launch {
                                    savingCode = "new"
                                    error = null
                                    try {
                                        AdminRepo.api.createPaymentMethodType(
                                            AdminPaymentMethodTypeCreate(
                                                code = newCode.trim(),
                                                title = newTitle.trim(),
                                            ),
                                        )
                                        newCode = ""
                                        newTitle = ""
                                        creatingType = false
                                        success = "Tipo de pago creado."
                                        reload()
                                    } catch (e: Exception) {
                                        error = e.userMessage("No se pudo crear el tipo de pago.")
                                    } finally {
                                        savingCode = null
                                    }
                                }
                            },
                            enabled = savingCode == null && newCode.isNotBlank() && newTitle.isNotBlank(),
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text("Crear tipo", fontWeight = FontWeight.SemiBold)
                        }
                    }
                }

                methods.forEach { method ->
                    val draft = drafts[method.code] ?: return@forEach
                    val expanded = expandedCode == method.code
                    val holder = imageHolders.getOrPut(method.code) { PickedMediaHolder() }
                    val localBytes = holder.payload?.bytes

                    Column(
                        Modifier
                            .fillMaxWidth()
                            .padding(bottom = 12.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
                            .background(AdminColors.Surface)
                            .padding(14.dp),
                    ) {
                        Row(
                            Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Row(
                                Modifier
                                    .weight(1f)
                                    .clickable { expandedCode = if (expanded) null else method.code },
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                PaymentMethodIconPreview(
                                    title = draft.title,
                                    remoteUrl = draft.imageUrl,
                                    localBytes = localBytes,
                                    revision = imageRevision,
                                )
                                Spacer(Modifier.width(12.dp))
                                Column(Modifier.weight(1f)) {
                                    Text(draft.title, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                                    Text(
                                        method.code,
                                        color = AdminColors.TextMuted,
                                        fontSize = 12.sp,
                                    )
                                    Text(
                                        if (draft.enabled) "Activo" else "Inactivo",
                                        color = if (draft.enabled) AdminColors.Primary else AdminColors.TextMuted,
                                        fontSize = 12.sp,
                                    )
                                }
                                Icon(
                                    if (expanded) Icons.Outlined.ExpandLess else Icons.Outlined.ExpandMore,
                                    contentDescription = null,
                                    tint = AdminColors.TextMuted,
                                )
                            }
                            Icon(
                                Icons.Outlined.Delete,
                                contentDescription = "Eliminar",
                                tint = AdminColors.Error,
                                modifier = Modifier
                                    .padding(start = 8.dp)
                                    .size(22.dp)
                                    .clickable(enabled = savingCode == null) {
                                        deleteConfirmCode = method.code
                                    },
                            )
                        }

                        AnimatedVisibility(expanded) {
                            Column(Modifier.padding(top = 14.dp)) {
                                PaymentMethodImageRow(
                                    title = draft.title,
                                    remoteUrl = draft.imageUrl,
                                    localBytes = localBytes,
                                    revision = imageRevision,
                                    enabled = savingCode == null,
                                    onPick = {
                                        openImagePicker { picked ->
                                            if (picked != null) {
                                                val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                                                holder.set(payload, picked.fileName)
                                                imageRevision++
                                            }
                                        }
                                    },
                                )
                                Spacer(Modifier.height(12.dp))
                                PaymentMethodField("Título", draft.title) {
                                    drafts[method.code] = draft.copy(title = it)
                                }
                                Spacer(Modifier.height(10.dp))
                                PaymentMethodField("Descripción", draft.description, singleLine = false) {
                                    drafts[method.code] = draft.copy(description = it)
                                }
                                Spacer(Modifier.height(10.dp))
                                PaymentMethodField("Etiqueta del monto", draft.amountLabel) {
                                    drafts[method.code] = draft.copy(amountLabel = it)
                                }
                                Spacer(Modifier.height(8.dp))
                                PaymentMethodCheckbox("Habilitado en recepción", draft.enabled) {
                                    drafts[method.code] = draft.copy(enabled = it)
                                }
                                PaymentMethodCheckbox("Aprobar al guardar (sin revisión manual)", draft.autoApprove) {
                                    drafts[method.code] = draft.copy(autoApprove = it)
                                }
                                PaymentMethodCheckbox("Pedir referencia", draft.requiresReference) {
                                    drafts[method.code] = draft.copy(requiresReference = it)
                                }
                                PaymentMethodCheckbox("Pedir comprobante (imagen)", draft.requiresProof) {
                                    drafts[method.code] = draft.copy(requiresProof = it)
                                }
                                PaymentMethodCheckbox("Cobro en moneda extranjera (tipo Divisas)", draft.usesForeignCurrency) {
                                    drafts[method.code] = draft.copy(usesForeignCurrency = it)
                                }
                                if (draft.usesForeignCurrency) {
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodField("Moneda extranjera (USD)", draft.foreignCurrencyCode) {
                                        drafts[method.code] = draft.copy(foreignCurrencyCode = it.uppercase())
                                    }
                                }

                                if (method.isStripe) {
                                    Spacer(Modifier.height(16.dp))
                                    Text("Configuración Stripe (API)", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodCheckbox("Stripe habilitado", stripeEnabled) { stripeEnabled = it }
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodField("Clave pública", stripePublicKey) { stripePublicKey = it }
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodSecretField("Clave secreta", stripeSecretKey) { stripeSecretKey = it }
                                }

                                if (method.isPaypal) {
                                    Spacer(Modifier.height(16.dp))
                                    Text("Configuración PayPal (API)", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodCheckbox("PayPal habilitado", paypalEnabled) { paypalEnabled = it }
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodField("Client ID", paypalClientId) { paypalClientId = it }
                                    Spacer(Modifier.height(8.dp))
                                    PaymentMethodSecretField("Secreto", paypalSecret) { paypalSecret = it }
                                    Spacer(Modifier.height(8.dp))
                                    Box {
                                        OutlinedButton(
                                            onClick = { modeMenu = true },
                                            modifier = Modifier.fillMaxWidth(),
                                            shape = RoundedCornerShape(10.dp),
                                        ) {
                                            Text("Modo: ${paypalMode.labelEs()}", modifier = Modifier.fillMaxWidth())
                                        }
                                        DropdownMenu(expanded = modeMenu, onDismissRequest = { modeMenu = false }) {
                                            AdminPaypalMode.entries.forEach { m ->
                                                DropdownMenuItem(
                                                    text = { Text(m.labelEs()) },
                                                    onClick = {
                                                        paypalMode = m
                                                        modeMenu = false
                                                    },
                                                )
                                            }
                                        }
                                    }
                                }

                                Spacer(Modifier.height(14.dp))
                                Button(
                                    onClick = {
                                        scope.launch {
                                            savingCode = method.code
                                            error = null
                                            success = null
                                            try {
                                                val current = drafts[method.code] ?: draft
                                                var updated = AdminRepo.api.updatePaymentMethodType(
                                                    method.code,
                                                    AdminPaymentMethodTypeUpsert(
                                                        title = current.title,
                                                        description = current.description.ifBlank { null },
                                                        enabled = current.enabled,
                                                        sortOrder = current.sortOrder,
                                                        requiresPhone = current.requiresPhone,
                                                        requiresReference = current.requiresReference,
                                                        requiresProof = current.requiresProof,
                                                        autoApprove = current.autoApprove,
                                                        amountLabel = current.amountLabel.ifBlank { null },
                                                        usesForeignCurrency = current.usesForeignCurrency,
                                                        foreignCurrencyCode = current.foreignCurrencyCode.ifBlank { null },
                                                    ),
                                                )
                                                holder.payload?.let { image ->
                                                    updated = AdminRepo.api.uploadPaymentMethodTypeImage(method.code, image)
                                                    holder.payload = null
                                                    imageRevision++
                                                }
                                                if (method.isStripe || method.isPaypal) {
                                                    AdminRepo.api.upsertPaymentMethodGateways(
                                                        AdminSettingsPaymentUpsert(
                                                            paypalEnabled = paypalEnabled,
                                                            paypalClientId = paypalClientId,
                                                            paypalSecret = paypalSecret,
                                                            paypalMode = paypalMode,
                                                            stripeEnabled = stripeEnabled,
                                                            stripePublicKey = stripePublicKey,
                                                            stripeSecretKey = stripeSecretKey,
                                                        ),
                                                    )
                                                }
                                                drafts[method.code] = current.copy(imageUrl = updated.imageUrl)
                                                methods = methods.map { if (it.code == method.code) updated else it }
                                                success = "${current.title} guardado."
                                            } catch (e: Exception) {
                                                error = e.userMessage("No se pudo guardar ${draft.title}.")
                                            } finally {
                                                savingCode = null
                                            }
                                        }
                                    },
                                    enabled = savingCode == null,
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(10.dp),
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = AdminColors.Primary,
                                        contentColor = AdminColors.OnBrand,
                                    ),
                                ) {
                                    Text(
                                        if (savingCode == method.code) "Guardando…" else "Guardar",
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                }
                                Spacer(Modifier.height(8.dp))
                                OutlinedButton(
                                    onClick = { deleteConfirmCode = method.code },
                                    enabled = savingCode == null,
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(10.dp),
                                ) {
                                    Text("Eliminar tipo", color = AdminColors.Error)
                                }
                            }
                        }
                    }
                }

                deleteConfirmCode?.let { code ->
                    val method = methods.find { it.code == code }
                    val title = drafts[code]?.title ?: method?.title ?: code
                    AlertDialog(
                        onDismissRequest = { deleteConfirmCode = null },
                        title = { Text("Eliminar tipo de pago") },
                        text = {
                            Text(
                                "¿Eliminar «$title»?\n\n" +
                                    "Si ya hay cobros registrados con este método, no se podrá eliminar y deberás desactivarlo en su lugar.",
                            )
                        },
                        confirmButton = {
                            TextButton(
                                onClick = {
                                    scope.launch {
                                        savingCode = code
                                        error = null
                                        success = null
                                        deleteConfirmCode = null
                                        try {
                                            AdminRepo.api.deletePaymentMethodType(code)
                                            success = "«$title» eliminado."
                                            reload()
                                        } catch (e: Exception) {
                                            error = e.userMessage("No se pudo eliminar «$title».")
                                        } finally {
                                            savingCode = null
                                        }
                                    }
                                },
                                enabled = savingCode == null,
                            ) {
                                Text("Eliminar", color = AdminColors.Error)
                            }
                        },
                        dismissButton = {
                            TextButton(onClick = { deleteConfirmCode = null }) {
                                Text("Cancelar")
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun PaymentMethodIconPreview(
    title: String,
    remoteUrl: String?,
    localBytes: ByteArray?,
    revision: Int,
) {
    val model: Any? = when {
        localBytes != null && localBytes.isNotEmpty() -> localBytes
        !remoteUrl.isNullOrBlank() -> {
            val sep = if (remoteUrl.contains('?')) '&' else '?'
            "$remoteUrl${sep}v=$revision"
        }
        else -> null
    }
    Box(
        Modifier
            .size(48.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(AdminColors.SurfaceContainer),
        contentAlignment = Alignment.Center,
    ) {
        if (model != null) {
            key(model, revision) {
                AsyncImage(
                    model = model,
                    contentDescription = title,
                    modifier = Modifier.fillMaxSize().padding(4.dp),
                    contentScale = ContentScale.Fit,
                )
            }
        } else {
            Text(title.take(1).uppercase(), color = AdminColors.Primary, fontWeight = FontWeight.Bold)
        }
    }
}

@Composable
private fun PaymentMethodImageRow(
    title: String,
    remoteUrl: String?,
    localBytes: ByteArray?,
    revision: Int,
    enabled: Boolean,
    onPick: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .border(1.dp, AdminColors.Outline, RoundedCornerShape(10.dp))
            .padding(10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        PaymentMethodIconPreview(title, remoteUrl, localBytes, revision)
        Spacer(Modifier.width(12.dp))
        Column(Modifier.weight(1f)) {
            Text("Imagen del método", fontWeight = FontWeight.Medium, fontSize = 13.sp)
            Text("Icono mostrado al cobrar (opcional).", color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        FileUploadIconButton(
            onClick = onPick,
            tooltip = "Subir imagen",
            enabled = enabled,
            tint = AdminColors.Primary,
        )
    }
}

@Composable
private fun PaymentMethodField(
    label: String,
    value: String,
    singleLine: Boolean = true,
    onChange: (String) -> Unit,
) {
    Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    Spacer(Modifier.height(4.dp))
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = singleLine,
        minLines = if (singleLine) 1 else 2,
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun PaymentMethodSecretField(label: String, value: String, onChange: (String) -> Unit) {
    Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    Spacer(Modifier.height(4.dp))
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        visualTransformation = if (visible) VisualTransformation.None else PasswordVisualTransformation(),
        trailingIcon = { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun PaymentMethodCheckbox(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onChange(!checked) }
            .padding(vertical = 2.dp),
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = onChange,
            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
        )
        Text(label, fontSize = 13.sp, color = AdminColors.Text)
    }
}
